'use client';

/**
 * RunbookModeSwitcher - Admin-only pill in the header for instant runbook switching.
 *
 * Shows the active mode abbreviation (e.g. "TPS", "CBSA", "EU") as a compact
 * clickable pill. On click, opens a menu listing all available runbooks (the shared DropdownMenu, look="standard",
 * one radio row per runbook: std-overlays G14).
 * Selecting one calls PUT /api/admin/runbook-mode, then router.refresh() to
 * re-render the RunbookConfig server component instantly.
 *
 * Visible only to platform admins. Other roles see nothing.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useIsAdmin } from '@/components/shell/AppShellContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SWITCHER_INVALIDATE_KEY = 'runbook-mode-changed-at';
const SWITCHER_INVALIDATE_WINDOW_MS = 90_000;

// ─── Types ────────────────────────────────────────────────────────────────────

interface RunbookOption {
  id: string;
  name: string;
  dbName: string;
  metadata: Record<string, string>;
  is_active: boolean;
}

interface ActiveRunbook {
  id: string;
  name: string;
  metadata: Record<string, string>;
}

// ─── Abbreviation helper ──────────────────────────────────────────────────────

function getAbbreviation(runbook: { id: string; metadata: Record<string, string> }): string {
  // Prefer the organization abbreviation from metadata
  if (runbook.metadata?.organization_abbreviation) {
    return runbook.metadata.organization_abbreviation;
  }
  // Fall back to uppercased first letters of the id segments
  return runbook.id
    .split('-')
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RunbookModeSwitcher() {
  const isAdmin = useIsAdmin();
  const router = useRouter();

  const [active, setActive] = useState<ActiveRunbook | null>(null);
  const [available, setAvailable] = useState<RunbookOption[]>([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const refocusPillRef = useRef(false);

  // Read initial active runbook from injected window config (set by RunbookConfig server component)
  useEffect(() => {
    const injected = window.__RUNBOOK_CONFIG__?.__activeRunbook;
    if (injected) setActive(injected);
  }, []);

  // Fetch available runbooks on mount (admin-only).
  // Endpoint has Cache-Control: private, max-age=60, stale-while-revalidate=300.
  // template.tsx remounts AppShell on every SPA nav, so this effect re-fires
  // each navigation — we ride the browser cache for the GET (0ms repeat cost).
  // BUT: after a PUT, the cached GET would be stale and would overwrite our
  // fresh local state. We use a sessionStorage marker (survives remount) to
  // force a cache-bust for up to 90s after a switch.
  useEffect(() => {
    if (!isAdmin) return;
    let aborted = false;
    // sessionStorage can throw SecurityError under strict ITP / corporate
    // policy. Treat any failure as "no marker" — fall through to cached fetch.
    let changedAt = 0;
    try {
      if (typeof window !== 'undefined') {
        changedAt = parseInt(sessionStorage.getItem(SWITCHER_INVALIDATE_KEY) || '0', 10);
      }
    } catch { /* sessionStorage blocked — proceed with default cache */ }
    const recentlyChanged = Date.now() - changedAt < SWITCHER_INVALIDATE_WINDOW_MS;
    const init: RequestInit = recentlyChanged ? { cache: 'reload' } : {};
    fetch('/api/admin/runbook-mode', init)
      .then((r) => r.json())
      .then((data) => {
        if (aborted) return;
        if (data.available) setAvailable(data.available);
        if (data.active) setActive(data.active);
        // Once we've seen fresh data, drop the invalidation marker so the
        // next mount (within max-age) can use the cache normally.
        if (recentlyChanged && typeof window !== 'undefined') {
          try { sessionStorage.removeItem(SWITCHER_INVALIDATE_KEY); } catch {}
        }
      })
      .catch(() => {/* silent — non-critical */});
    return () => { aborted = true; };
  }, [isAdmin]);

  // Escape, an outside press and a choice are handled by the shared menu; focus returns to the pill after Escape or a
  // choice (WCAG 2.1.1, 2.4.3).

  // The pill is disabled while switching, and a disabled button cannot take focus: give it back once it re-enables.
  useEffect(() => {
    if (switching || !refocusPillRef.current) return;
    refocusPillRef.current = false;
    pillRef.current?.focus();
  }, [switching]);

  if (!isAdmin) return null;
  if (!active && available.length === 0) return null;

  const abbreviation = active ? getAbbreviation(active) : '…';

  async function switchTo(dbName: string) {
    if (switching) return;
    if (menuRef.current?.contains(document.activeElement)) refocusPillRef.current = true;
    setSwitching(true);
    setOpen(false);
    try {
      const res = await fetch('/api/admin/runbook-mode', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: dbName }),
      });
      const data = await res.json();
      if (data.success && data.active) {
        setActive(data.active);
        // Update is_active flags in the available list so the dropdown highlights correctly
        setAvailable((prev) =>
          prev.map((rb) => ({ ...rb, is_active: rb.dbName === dbName }))
        );
        // Marker survives template-driven remount; the mount effect uses it
        // to cache-bust the next GET so we don't read a pre-switch response
        // from the browser cache and overwrite local state with stale data.
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(SWITCHER_INVALIDATE_KEY, String(Date.now()));
          } catch {/* sessionStorage blocked — fall back to 60s cache window */}
        }
      }
    } catch {
      // Silent — the poll will self-correct within 15s
    } finally {
      // Re-enable the pill before triggering refresh — router.refresh() can suspend
      // the component tree while waiting for the server response, which would leave
      // the pill stuck in disabled/spinner state if setSwitching ran after.
      setSwitching(false);
    }
    // Trigger full server component re-render AFTER local state is settled.
    // This runs outside try/finally so the pill is already interactive.
    router.refresh();
  }

  const activeDbName = available.find((rb) => rb.is_active)?.dbName ?? '';

  return (
    <DropdownMenu look="standard" modal={false} open={open} onOpenChange={setOpen}>
      {/* Pill button */}
      <DropdownMenuTrigger asChild>
        <button
          ref={pillRef}
          disabled={switching}
          title={active?.name ?? 'Switch runbook mode'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border transition-all duration-150
            bg-indigo-500/10 border-indigo-500/30 text-indigo-300
            hover:bg-indigo-500/20 hover:border-indigo-400/50
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {switching ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : null}
          <span>{abbreviation}</span>
          <ChevronDown className={`w-3 h-3 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent ref={menuRef} align="end" sideOffset={6} aria-label="Runbook Mode" className="w-popup max-h-[calc(100dvh-5rem)]">
        <DropdownMenuLabel>Runbook Mode</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={activeDbName}>
          {available.map((rb) => {
            const abbr = getAbbreviation(rb);
            const isActive = rb.is_active;
            return (
              <DropdownMenuRadioItem
                key={rb.dbName}
                value={rb.dbName}
                disabled={isActive || switching}
                data-runbook={rb.dbName}
                className="py-1.5 data-[disabled]:opacity-100"
                onSelect={() => { if (!isActive) void switchTo(rb.dbName); }}
              >
                {/* Abbreviation badge */}
                <span className="flex size-control-sm shrink-0 items-center justify-center rounded-item bg-raised text-chip text-ink-secondary">
                  {abbr}
                </span>

                {/* Labels */}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{rb.name}</span>
                  <span className="block truncate text-meta text-ink-secondary">
                    {rb.metadata?.organization ?? rb.dbName}
                  </span>
                </span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
